import { useEffect, useState } from 'react';

export function useScript(url: string) {
  useEffect(() => {
    const script = document.createElement('script');

    script.src = url;
    script.async = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [url]);
}

export function useSimpleFetch<T = any>(url: string) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | Error>(null);
  const [data, setData] = useState<T | null>(null);

  useEffect(() => {
    const getData = async () => {
      try {
        const res = await fetch(url);
        setData((await res.json()) as T);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [url]);

  return { loading, error, data };
}

export function useMedia<T = any>(
  queries: Array<string>,
  values: Array<T>,
  defaultValue: T
) {
  // Array containing a media query list for each query
  const mediaQueryLists =
    typeof window === 'undefined'
      ? []
      : queries.map((q) => window.matchMedia(q));

  // Function that gets value based on matching media query
  const getValue = () => {
    // Get index of first media query that matches
    const index = mediaQueryLists.findIndex((mql) => mql.matches);
    // Return related value or defaultValue if none
    return typeof values[index] !== 'undefined' ? values[index] : defaultValue;
  };

  // State and setter for matched value
  const [value, setValue] = useState(defaultValue);

  useEffect(
    () => {
      setValue(getValue());
      // Event listener callback
      // Note: By defining getValue outside of useEffect we ensure that it has ...
      // ... current values of hook args (as this hook callback is created once on mount).
      const handler = () => setValue(getValue);
      // Set a listener for each media query with above handler as callback.
      mediaQueryLists.forEach((mql) => {
        const useNewListener = typeof mql.addEventListener !== 'undefined';
        if (useNewListener) {
          mql.addEventListener('change', handler);
        } else {
          mql.addListener(handler);
        }
      });
      // Remove listeners on cleanup
      return () =>
        mediaQueryLists.forEach((mql) => {
          const useNewListener = typeof mql.removeEventListener !== 'undefined';
          if (useNewListener) {
            mql.removeEventListener('change', handler);
          } else {
            mql.removeListener(handler);
          }
        });
    },
    [] // Empty array ensures effect is only run on mount and unmount
  );

  return value;
}
